import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Boxes, Clapperboard, IndianRupee, Package, Pencil, Plus, Receipt, Ruler, DollarSign, Image as ImageIcon, PanelTop, Layers, Shirt, Scale, ArrowDownToLine, ShoppingCart, X } from 'lucide-react';
import { ItemPricesSection } from './ItemPricesSection';
import { ItemImagesTab } from './ItemImagesTab';
import { ItemHeroTab } from './ItemHeroTab';
import { ItemVideoTab } from './ItemVideoTab';
import { ItemBannersTab } from './ItemBannersTab';
import { ItemVariantsSection } from './ItemVariantsSection';
import { CategoryAttributesField } from './CategoryAttributesField';
import { FabricCareFields, normaliseFabricCareValue } from './ItemFabricCareTab';
import { ItemUnitsSection } from './ItemUnitsSection';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DIALOG_TAB_TRIGGER, DialogEyebrow } from '@/components/common/DialogPrimitives';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TabsContent, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  categoriesService,
  inventoryService,
  type ItemCreatePayload,
} from '@/services/inventory';
import { itemAttributeTemplateService } from '@/services/platform/item-attribute-template';
import type {
  DimensionUnit,
  InventoryItem,
  ItemCatalogStatus,
  ItemKind,
  TaxPreference,
  WeightUnit,
} from '@/types/inventory';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the form edits the item; otherwise it creates a new one. */
  item: InventoryItem | null;
  /** Classification a freshly-created item defaults to (e.g. RAW_MATERIAL
   *  when creating from the Raw Materials view). Ignored when editing. */
  defaultKind?: ItemKind;
}

const EMPTY: ItemCreatePayload = {
  sku: '',
  name: '',
  description: '',
  unitOfMeasure: 'EA',
  hsnCode: '',
  categoryId: undefined,
  reorderPoint: undefined,
  status: 'ACTIVE',
  itemType: 'GOODS',
  kind: 'FINISHED_GOOD',
  trackInventory: true,
  brand: '',
  manufacturer: '',
  isFeatured: false,
  isNewArrival: false,
  hasVariants: false,
  tracksBatches: false,
  isBundle: false,
  taxPreference: 'TAXABLE',
  intraStateTaxRate: undefined,
  interStateTaxRate: undefined,
  sellingPrice: undefined,
  mrp: undefined,
  salesDescription: '',
  costPrice: undefined,
  purchaseDescription: '',
  dimensionLength: undefined,
  dimensionWidth: undefined,
  dimensionHeight: undefined,
  dimensionUnit: 'CM',
  weight: undefined,
  weightUnit: 'KG',
  packagedWeight: undefined,
  unitsPerCarton: undefined,
  fragile: false,
  hazmat: false,
  fabricComposition: [],
  washCareInstructions: [],
  careNotes: '',
};

// Unit-of-measure catalogue. Codes match the NIC GST e-invoicing
// standard so what's chosen here flows straight into IRN submission
// (J6 when wired). Most-common-for-retail items (PCS, NOS, SET, PRS)
// land at the top of the "Counted goods" group — that's the right
// pick for clothing, shoes, accessories.
const UOM_GROUPS: { label: string; options: { value: string; label: string }[] }[] = [
  {
    label: 'Counted goods',
    options: [
      { value: 'PCS', label: 'Pieces (PCS)' },
      { value: 'NOS', label: 'Numbers (NOS)' },
      { value: 'EA', label: 'Each (EA)' },
      { value: 'SET', label: 'Sets (SET)' },
      { value: 'PRS', label: 'Pairs (PRS)' },
      { value: 'DOZ', label: 'Dozen (DOZ)' },
      { value: 'UNT', label: 'Units (UNT)' },
    ],
  },
  {
    label: 'Weight',
    options: [
      { value: 'KGS', label: 'Kilograms (KGS)' },
      { value: 'GMS', label: 'Grams (GMS)' },
      { value: 'TON', label: 'Tonnes (TON)' },
      { value: 'QTL', label: 'Quintal (QTL)' },
      { value: 'MTS', label: 'Metric Ton (MTS)' },
    ],
  },
  {
    label: 'Volume',
    options: [
      { value: 'LTR', label: 'Litres (LTR)' },
      { value: 'MLT', label: 'Millilitres (MLT)' },
      { value: 'KLR', label: 'Kilolitres (KLR)' },
      { value: 'CBM', label: 'Cubic Metres (CBM)' },
      { value: 'CCM', label: 'Cubic Centimetres (CCM)' },
    ],
  },
  {
    label: 'Length',
    options: [
      { value: 'MTR', label: 'Metres (MTR)' },
      { value: 'CMS', label: 'Centimetres (CMS)' },
      { value: 'KME', label: 'Kilometres (KME)' },
      { value: 'YDS', label: 'Yards (YDS)' },
    ],
  },
  {
    label: 'Area',
    options: [
      { value: 'SQM', label: 'Square Metres (SQM)' },
      { value: 'SQF', label: 'Square Feet (SQF)' },
      { value: 'SQY', label: 'Square Yards (SQY)' },
    ],
  },
  {
    label: 'Packaging',
    options: [
      { value: 'BOX', label: 'Box (BOX)' },
      { value: 'BAG', label: 'Bag (BAG)' },
      { value: 'BDL', label: 'Bundle (BDL)' },
      { value: 'CTN', label: 'Carton (CTN)' },
      { value: 'PAC', label: 'Pack (PAC)' },
      { value: 'ROL', label: 'Roll (ROL)' },
      { value: 'DRM', label: 'Drum (DRM)' },
      { value: 'BTL', label: 'Bottle (BTL)' },
      { value: 'TUB', label: 'Tube (TUB)' },
      { value: 'CAN', label: 'Can (CAN)' },
      { value: 'TBS', label: 'Tablets (TBS)' },
    ],
  },
  {
    label: 'Service / other',
    options: [
      { value: 'HR', label: 'Hour (HR)' },
      { value: 'DAY', label: 'Day (DAY)' },
      { value: 'OTH', label: 'Other (OTH)' },
    ],
  },
];

const STATUS_OPTIONS: { value: ItemCatalogStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DISCONTINUED', label: 'Discontinued' },
  { value: 'ARCHIVED', label: 'Archived' },
];

// Hints reference "tax" generically; regime-specific phrasing
// (GST / VAT / sales tax) is left to the rate-entry field below.
const TAX_PREF_OPTIONS: { value: TaxPreference; label: string; hint: string }[] = [
  { value: 'TAXABLE',      label: 'Taxable',       hint: 'Standard rate applies' },
  { value: 'NON_TAXABLE',  label: 'Non-taxable',   hint: 'Outside the tax regime' },
  { value: 'EXEMPT',       label: 'Exempt',        hint: 'Statutorily exempted' },
  { value: 'OUT_OF_SCOPE', label: 'Out of scope',  hint: 'Not subject to tax' },
];

const GST_SLABS = [0, 5, 12, 18, 28];

export function ItemFormSheet({ open, onOpenChange, item, defaultKind }: Props) {
  const qc = useQueryClient();
  const me = useCurrentUser();
  // Tenant config drives currency labels + tax-tab shape. Fall back to
  // INR + INDIA_GST so the form still renders something for the (very rare)
  // case the user object hasn't hydrated yet.
  const tenantCurrency = me?.tenantCurrency ?? 'INR';
  const tenantTaxRegime = me?.tenantTaxRegime ?? 'INDIA_GST';
  const isIndiaGst = tenantTaxRegime === 'INDIA_GST';
  const isEdit = !!item;
  const [form, setForm] = useState<ItemCreatePayload>(EMPTY);
  const [tab, setTab] = useState('general');
  const [hsnRateHint, setHsnRateHint] = useState<string | null>(null);
  // S3 — alternate UoMs (ItemUnit) are a post-create sub-resource; when
  // creating we buffer them here and POST after the item exists.
  const [draftUnits, setDraftUnits] = useState<DraftUnit[]>([]);

  // On HSN blur, look the code up in the platform HSN→GST master and
  // auto-fill the tax rate. Suggestion only — the operator can override.
  const applyHsnRate = async (hsn: string) => {
    const code = hsn.trim();
    if (!code) {
      setHsnRateHint(null);
      return;
    }
    try {
      const match = await inventoryService.lookupHsnRate(code);
      if (match) {
        setForm((f) => ({
          ...f,
          intraStateTaxRate: match.gstRate,
          interStateTaxRate: match.gstRate,
        }));
        setHsnRateHint(
          `GST ${match.gstRate}% suggested from HSN ${match.matchedCode} — ${match.description}`,
        );
      } else {
        setHsnRateHint(null);
      }
    } catch {
      // Lookup is best-effort; a failure just leaves the rate as-is.
    }
  };
  // L2 — SKU rename confirm. Stores the {old, new} so the dialog can
  // render the diff inline; the form's submit handler opens the
  // dialog and the dialog's onConfirm fires the actual mutation.
  const [renameConfirm, setRenameConfirm] = useState<{
    oldSku: string;
    newSku: string;
    childCount: number;
  } | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: () => categoriesService.list(),
    enabled: open,
    staleTime: 60_000,
  });
  const tenantTemplateQuery = useQuery({
    queryKey: ['tenancy', 'item-attribute-template'],
    queryFn: () => itemAttributeTemplateService.get(),
    enabled: open,
    staleTime: 60_000,
  });
  const tenantAttrs = tenantTemplateQuery.data?.itemAttributes ?? [];

  useEffect(() => {
    if (!open) return;
    setTab('general');
    if (item) {
      setForm({
        sku: item.sku,
        name: item.name,
        description: item.description ?? '',
        unitOfMeasure: item.unitOfMeasure,
        hsnCode: item.hsnCode ?? '',
        categoryId: item.categoryId ?? undefined,
        attributes: item.attributes ?? {},
        reorderPoint: item.reorderPoint ?? undefined,
        status: item.status,
        itemType: item.itemType,
        kind: item.kind ?? 'FINISHED_GOOD',
        trackInventory: item.trackInventory,
        brand: item.brand ?? '',
        manufacturer: item.manufacturer ?? '',
        isFeatured: item.isFeatured ?? false,
        isNewArrival: item.isNewArrival ?? false,
        hasVariants: item.hasVariants ?? false,
        tracksBatches: item.tracksBatches ?? false,
        isBundle: item.isBundle ?? false,
        taxPreference: item.taxPreference,
        intraStateTaxRate: item.intraStateTaxRate ?? undefined,
        interStateTaxRate: item.interStateTaxRate ?? undefined,
        sellingPrice: item.sellingPrice ?? undefined,
        mrp: item.mrp ?? undefined,
        salesDescription: item.salesDescription ?? '',
        costPrice: item.costPrice ?? undefined,
        purchaseDescription: item.purchaseDescription ?? '',
        dimensionLength: item.dimensionLength ?? undefined,
        dimensionWidth: item.dimensionWidth ?? undefined,
        dimensionHeight: item.dimensionHeight ?? undefined,
        dimensionUnit: item.dimensionUnit,
        weight: item.weight ?? undefined,
        weightUnit: item.weightUnit,
        packagedWeight: item.packagedWeight ?? undefined,
        unitsPerCarton: item.unitsPerCarton ?? undefined,
        fragile: item.fragile ?? false,
        hazmat: item.hazmat ?? false,
        fabricComposition: item.fabricComposition ?? [],
        washCareInstructions: item.washCareInstructions ?? [],
        careNotes: item.careNotes ?? '',
      });
    } else {
      setForm({ ...EMPTY, kind: defaultKind ?? 'FINISHED_GOOD' });
    }
    // Alternate UoMs are a post-create sub-resource (see submit handler);
    // reset the draft list whenever the sheet (re)opens. In edit mode the
    // live ItemUnitsSection owns them instead, so this stays empty.
    setDraftUnits([]);
  }, [open, item, defaultKind]);

  const isService = form.itemType === 'SERVICE';

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: ItemCreatePayload = {
        ...form,
        sku: form.sku.trim().toUpperCase(),
        name: form.name.trim(),
        description: emptyToUndef(form.description),
        hsnCode: emptyToUndef(form.hsnCode),
        brand: emptyToUndef(form.brand),
        manufacturer: emptyToUndef(form.manufacturer),
        isFeatured: form.isFeatured ?? false,
        isNewArrival: form.isNewArrival ?? false,
        hasVariants: form.hasVariants ?? false,
        salesDescription: emptyToUndef(form.salesDescription),
        purchaseDescription: emptyToUndef(form.purchaseDescription),
        categoryId: form.categoryId || undefined,
        unitOfMeasure: (form.unitOfMeasure || 'EA').trim(),
        // Services never track inventory.
        trackInventory: isService ? false : (form.trackInventory ?? true),
        // Only physical goods carry a raw/finished classification.
        kind: isService ? 'FINISHED_GOOD' : (form.kind ?? 'FINISHED_GOOD'),
        // S3 — trim/drop empty fabric + care rows so create and edit
        // serialise identically (same helper the detail-sheet tab uses).
        ...normaliseFabricCareValue({
          fabric: form.fabricComposition ?? [],
          careInstructions: form.washCareInstructions ?? [],
          careNotes: form.careNotes ?? '',
        }),
      };
      if (isEdit && item) {
        // L2 — include SKU on the patch; the BE cascades the rename to
        // child variants atomically when it changes. Confirm dialog
        // upstream gates the rename so it can't fire by accident.
        return inventoryService.update(item.id, payload);
      }
      const created = await inventoryService.create(payload);
      // S3 — alternate UoMs are a separate sub-resource that needs the new
      // item id. The item is already saved, so a unit POST failure must NOT
      // fail the whole create — collect the failures and warn instead.
      const baseUnit = payload.unitOfMeasure ?? 'EA';
      const validUnits = draftUnits.filter((u) => draftUnitValid(u, baseUnit));
      if (validUnits.length) {
        const failed: string[] = [];
        for (const u of validUnits) {
          try {
            await inventoryService.createUnit(created.id, {
              code: u.code.trim().toUpperCase(),
              name: u.name.trim(),
              conversionFactor: Number(u.conversionFactor),
              isPurchaseDefault: u.isPurchaseDefault,
              isSalesDefault: u.isSalesDefault,
            });
          } catch {
            failed.push(u.code.trim().toUpperCase());
          }
        }
        if (failed.length) {
          toast.warning(
            `Item created, but ${failed.length} unit${failed.length === 1 ? '' : 's'} (${failed.join(', ')}) could not be saved — add ${failed.length === 1 ? 'it' : 'them'} from the item's Units section.`,
          );
        }
      }
      return created;
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Item updated' : 'Item created');
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.sku.trim() || !form.name.trim()) {
      setTab('general');
      return;
    }
    // L2 — renaming the parent SKU on an existing item cascades the
    // rename to every child variant. Gate it behind a confirm so a
    // mistyped SKU can't silently rewrite stock + barcode references.
    if (isEdit && item) {
      const newSku = form.sku.trim().toUpperCase();
      const oldSku = item.sku.toUpperCase();
      if (newSku !== oldSku) {
        setRenameConfirm({
          oldSku,
          newSku,
          childCount: item.variantCount ?? 0,
        });
        return;
      }
    }
    mutation.mutate();
  };

  const category = useMemo(
    () => (categoriesQuery.data ?? []).find((c) => c.id === item?.categoryId),
    [categoriesQuery.data, item?.categoryId],
  );
  /** The category currently SELECTED in the form (may differ from
   *  the item's saved category while the user is editing). Used to
   *  drive the dynamic attribute-dropdown section. */
  const formCategory = useMemo(
    () => (categoriesQuery.data ?? []).find((c) => c.id === form.categoryId),
    [categoriesQuery.data, form.categoryId],
  );

  const HeroIcon = isEdit ? Pencil : Plus;

  return (
    <>
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          {/* Hero badge — primary image on edit if available, otherwise a
              tinted icon square. Mirrors the detail dialog so popups read
              as one product. */}
          {isEdit && item?.primaryImageUrl ? (
            <img
              src={item.primaryImageUrl}
              alt=""
              className="h-14 w-14 shrink-0 rounded-lg border border-border/70 object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
            >
              {isEdit && item ? <Boxes className="h-6 w-6" /> : <HeroIcon className="h-6 w-6" />}
            </span>
          )}

          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>
              {isEdit ? `Edit item · ${item?.sku}` : 'New item'}
            </DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              {isEdit ? (item?.name ?? 'Edit item') : 'Create catalog item'}
            </DialogTitle>

            {isEdit && item ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                {item.status !== 'ACTIVE' && (
                  <Badge variant="muted" className="font-mono text-[10px]">
                    {item.status.toLowerCase()}
                  </Badge>
                )}
                <Badge variant="muted" className="text-[10px]">
                  {item.itemType === 'SERVICE' ? 'Service' : 'Goods'}
                </Badge>
                {category && (
                  <Badge variant="muted" className="text-[10px]">
                    <span className="font-mono">{category.code}</span>
                    <span className="ml-1">{category.name}</span>
                  </Badge>
                )}
                <Badge variant="muted" className="font-mono text-[10px]">
                  {item.unitOfMeasure}
                </Badge>
                {item.hsnCode && (
                  <Badge variant="muted" className="font-mono text-[10px]">
                    HSN {item.hsnCode}
                  </Badge>
                )}
                {item.tracksBatches && (
                  <Badge variant="muted" className="font-mono text-[10px]">batched</Badge>
                )}
              </div>
            ) : (
              <DialogDescription className="text-[12.5px] leading-relaxed">
                Create a new catalog item. Goods track stock per warehouse; services do not.
              </DialogDescription>
            )}
          </div>
        </div>
      }
      title=""
      description={
        isEdit
          ? 'Update catalog metadata. Stock quantities are mutated separately via the stock adjustment dialog.'
          : 'Create a new catalog item. Goods track stock per warehouse; services do not.'
      }
      submitLabel={isEdit ? 'Save changes' : 'Create item'}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="6xl"
      fixedHeight="xl"
      tabs={{
        value: tab,
        onValueChange: setTab,
        triggers: (
          <>
            <TabsTrigger value="general" className={DIALOG_TAB_TRIGGER}>
              <Package className="h-3.5 w-3.5" />
              General
            </TabsTrigger>
            <TabsTrigger value="images" className={DIALOG_TAB_TRIGGER}>
              <ImageIcon className="h-3.5 w-3.5" />
              Images
            </TabsTrigger>
            <TabsTrigger value="hero" className={DIALOG_TAB_TRIGGER}>
              <PanelTop className="h-3.5 w-3.5" />
              Hero Banner
            </TabsTrigger>
            <TabsTrigger value="variants" className={DIALOG_TAB_TRIGGER}>
              <Layers className="h-3.5 w-3.5" />
              Variants
            </TabsTrigger>
            <TabsTrigger value="prices" className={DIALOG_TAB_TRIGGER}>
              <DollarSign className="h-3.5 w-3.5" />
              Sale Prices
            </TabsTrigger>
            <TabsTrigger value="pricing" className={DIALOG_TAB_TRIGGER}>
              <IndianRupee className="h-3.5 w-3.5" />
              Costs
            </TabsTrigger>
            <TabsTrigger value="tax" className={DIALOG_TAB_TRIGGER}>
              <Receipt className="h-3.5 w-3.5" />
              Tax
            </TabsTrigger>
            <TabsTrigger value="fulfilment" className={DIALOG_TAB_TRIGGER}>
              <Ruler className="h-3.5 w-3.5" />
              Fulfilment
            </TabsTrigger>
            <TabsTrigger value="fabric" className={DIALOG_TAB_TRIGGER}>
              <Shirt className="h-3.5 w-3.5" />
              Fabric &amp; Care
            </TabsTrigger>
            <TabsTrigger value="units" className={DIALOG_TAB_TRIGGER}>
              <Scale className="h-3.5 w-3.5" />
              Units
            </TabsTrigger>
          </>
        ),
      }}
    >
      {/* ── General ─────────────────────────────────────────────── */}
      <TabsContent value="general" className="m-0 space-y-4">
          <FormField label="Type" htmlFor="itemType" required>
            <div className="grid grid-cols-2 gap-2" id="itemType">
              <TypeButton
                active={form.itemType === 'GOODS'}
                label="Goods"
                hint="Physical product. Tracks stock per warehouse."
                onClick={() => setForm((f) => ({ ...f, itemType: 'GOODS', trackInventory: true }))}
              />
              <TypeButton
                active={form.itemType === 'SERVICE'}
                label="Service"
                hint="Non-physical. No stock tracking."
                onClick={() => setForm((f) => ({ ...f, itemType: 'SERVICE', trackInventory: false }))}
              />
            </div>
          </FormField>

          {/* Procurement classification — raw material (bought on Purchase
              Requests, consumed by jobs) vs finished good (sellable). Only
              meaningful for physical GOODS. */}
          {form.itemType === 'GOODS' && (
            <FormField label="Classification" htmlFor="itemKind" required>
              <div className="grid grid-cols-2 gap-2" id="itemKind">
                <TypeButton
                  active={(form.kind ?? 'FINISHED_GOOD') === 'FINISHED_GOOD'}
                  label="Finished good"
                  hint="Sellable inventory shipped to customers."
                  onClick={() => setForm((f) => ({ ...f, kind: 'FINISHED_GOOD' }))}
                />
                <TypeButton
                  active={form.kind === 'RAW_MATERIAL'}
                  label="Raw material"
                  hint="Input a job consumes — cotton, zip, button. Selectable on Purchase Requests."
                  onClick={() => setForm((f) => ({ ...f, kind: 'RAW_MATERIAL' }))}
                />
              </div>
            </FormField>
          )}

          {/* K1 — variants opt-in. Off by default; turn on for items
              that come in multiple sizes / colours / fits. Each combo
              becomes its own SKU with separate stock + price + images. */}
          {form.itemType === 'GOODS' && (
            <div className="rounded-md border border-border/60 bg-muted/20 px-4 py-3">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!form.hasVariants}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, hasVariants: e.target.checked }))
                  }
                />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold">
                    This item has variants (size, colour, fit, …)
                  </div>
                  <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                    Turn on if the same product comes in multiple SKUs you
                    need to track separately. Stock, price overrides and
                    images are managed per variant in the{' '}
                    <strong>Variants</strong> tab. The parent item carries
                    only shared catalog metadata (name, brand, category,
                    tax, UoM).
                  </p>
                  {form.hasVariants && (
                    <p className="mt-1 text-[11px] text-info">
                      ✓ The <strong>Variants</strong> tab is now your main
                      editing surface. Save the parent here, then define
                      attributes and generate child SKUs there.
                    </p>
                  )}
                </div>
              </label>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="SKU"
              htmlFor="sku"
              required
              hint={
                isEdit
                  ? form.hasVariants
                    ? 'Acts as the style code for variants. Renaming cascades to all child SKUs.'
                    : 'Alphanumeric. _- allowed. Renaming affects stock + orders externally.'
                  : 'Alphanumeric. _- allowed.'
              }
            >
              <Input
                id="sku"
                value={form.sku}
                onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                placeholder={isService ? 'CONSULT-HR' : form.hasVariants ? '0001' : 'BX-CORR-L'}
                required
                autoComplete="off"
              />
              {isEdit && item && form.sku.trim().toUpperCase() !== item.sku.toUpperCase() && (
                <div className="mt-1 rounded-md border border-warning/30 bg-warning/10 px-2 py-1.5 text-[11px] text-warning-foreground">
                  <strong>Heads up:</strong> Saving will rename{' '}
                  <span className="font-mono">{item.sku}</span> →{' '}
                  <span className="font-mono">{form.sku.trim().toUpperCase()}</span>
                  {item.variantCount && item.variantCount > 0 ? (
                    <> and {item.variantCount} child variant{item.variantCount === 1 ? '' : 's'}</>
                  ) : null}
                  . You'll be asked to confirm.
                </div>
              )}
            </FormField>
            <FormField label="Name" htmlFor="name" required>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={isService ? 'Hourly consulting' : 'Corrugated Box Large'}
                required
                autoComplete="off"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <FormField label="Brand" htmlFor="brand">
              <Input
                id="brand"
                value={form.brand ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
                placeholder="Acme"
              />
            </FormField>
            <FormField label="Manufacturer" htmlFor="manufacturer">
              <Input
                id="manufacturer"
                value={form.manufacturer ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))}
                placeholder="Acme Industries Ltd"
              />
            </FormField>
            <FormField label="Category" htmlFor="category">
              <Select
                value={form.categoryId ?? '__none'}
                onValueChange={(v) => setForm((f) => ({ ...f, categoryId: v === '__none' ? undefined : v }))}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder={categoriesQuery.isLoading ? 'Loading…' : 'Uncategorised'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Uncategorised</SelectItem>
                  {(categoriesQuery.data ?? []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="font-mono text-[11px] text-muted-foreground">{c.code}</span>
                      <span className="ml-2">{c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          {/* Dynamic item-level attributes — union of category and
              tenant templates. Always renders so the operator can
              add tenant-wide custom fields even on un-templated
              categories. */}
          <CategoryAttributesField
            category={formCategory}
            tenantAttrs={tenantAttrs}
            value={form.attributes}
            onChange={(next) => setForm((f) => ({ ...f, attributes: next }))}
            onCreateOption={(attrName, created) => {
              const inCategory = formCategory?.attributeTemplate?.itemAttributes?.some(
                (a) => a.name === attrName,
              );
              if (inCategory) {
                qc.setQueryData<typeof categoriesQuery.data>(
                  ['inventory', 'categories'],
                  (prev) => {
                    if (!prev) return prev;
                    return prev.map((c) => {
                      if (c.id !== form.categoryId) return c;
                      const tpl = c.attributeTemplate ?? {
                        itemAttributes: [],
                        variantAxes: [],
                      };
                      const list = (tpl.itemAttributes ?? []).map((a) => {
                        if (a.name !== attrName) return a;
                        const opts = a.options ?? [];
                        if (opts.some((o) => o.toLowerCase() === created.toLowerCase())) {
                          return a;
                        }
                        return { ...a, options: [...opts, created] };
                      });
                      return {
                        ...c,
                        attributeTemplate: { ...tpl, itemAttributes: list },
                      };
                    });
                  },
                );
              } else {
                qc.setQueryData<typeof tenantTemplateQuery.data>(
                  ['tenancy', 'item-attribute-template'],
                  (prev) => {
                    const list = prev?.itemAttributes ?? [];
                    return {
                      itemAttributes: list.map((a) => {
                        if (a.name !== attrName) return a;
                        const opts = a.options ?? [];
                        if (opts.some((o) => o.toLowerCase() === created.toLowerCase())) {
                          return a;
                        }
                        return { ...a, options: [...opts, created] };
                      }),
                    };
                  },
                );
              }
            }}
            onCreateAttribute={(attr) => {
              qc.setQueryData<typeof tenantTemplateQuery.data>(
                ['tenancy', 'item-attribute-template'],
                (prev) => {
                  const list = prev?.itemAttributes ?? [];
                  if (list.some((a) => a.name.toLowerCase() === attr.name.toLowerCase())) {
                    return prev;
                  }
                  return {
                    itemAttributes: [
                      ...list,
                      {
                        name: attr.name,
                        options: attr.options,
                        multi: attr.multi,
                        required: false,
                      },
                    ],
                  };
                },
              );
            }}
          />

          {/* J3 — operator-curated merchandising flags. Surface on the
              portal's landing-page strips. Stays out of the way (small,
              checkbox-style) since most items won't be flagged. */}
          <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Portal merchandising
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex cursor-pointer items-start gap-2 text-[12.5px]">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!form.isFeatured}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isFeatured: e.target.checked }))
                  }
                />
                <span>
                  <span className="font-medium">Feature on portal</span>
                  <span className="block text-[11px] text-muted-foreground">
                    Surface in the Featured hero on your storefront landing.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-[12.5px]">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!form.isNewArrival}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isNewArrival: e.target.checked }))
                  }
                />
                <span>
                  <span className="font-medium">Mark as new arrival</span>
                  <span className="block text-[11px] text-muted-foreground">
                    Surface in the "New arrivals" rail. Toggle off once the
                    item is no longer fresh stock.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <FormField label="Description" htmlFor="description" hint="Short product description (visible to operators).">
            <Textarea
              id="description"
              value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Inner-fluted 5-ply, 30 × 30 × 30 cm"
              rows={2}
            />
          </FormField>

          {!isService && (
            <FormField
              label="Track batches / lots"
              htmlFor="tracksBatches"
              hint="Enable for items where mfg/expiry matters (FMCG, pharma, food). Stock is then tracked per-batch and FIFO suggestion uses earliest-expiry-first."
            >
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border/70 bg-card px-3 py-2 text-[12.5px]">
                <input
                  id="tracksBatches"
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={form.tracksBatches ?? false}
                  onChange={(e) => setForm((f) => ({ ...f, tracksBatches: e.target.checked }))}
                  disabled={form.isBundle ?? false}
                />
                <span className="font-medium">Track per-batch</span>
                <span className="text-muted-foreground">
                  · adds a Batches tab on the item
                </span>
              </label>
            </FormField>
          )}

          {!isService && (
            <FormField
              label="Composite item / bundle"
              htmlFor="isBundle"
              hint="Bundles consume their components on sale instead of carrying their own stock. Enable to define components on the item detail."
            >
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border/70 bg-card px-3 py-2 text-[12.5px]">
                <input
                  id="isBundle"
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={form.isBundle ?? false}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      isBundle: e.target.checked,
                      // Bundles never carry their own stock or batches.
                      trackInventory: e.target.checked ? false : f.trackInventory,
                      tracksBatches: e.target.checked ? false : f.tracksBatches,
                    }))
                  }
                />
                <span className="font-medium">This is a bundle</span>
                <span className="text-muted-foreground">
                  · disables stock tracking; surfaces a Components tab
                </span>
              </label>
            </FormField>
          )}

          <div className="grid grid-cols-3 gap-4">
            <FormField
              label="Unit of measure"
              htmlFor="uom"
              required
              hint="GST-portal standard codes. Pieces (PCS) is the convention for clothing / retail."
            >
              <Select
                value={form.unitOfMeasure ?? 'PCS'}
                onValueChange={(v) => setForm((f) => ({ ...f, unitOfMeasure: v }))}
              >
                <SelectTrigger id="uom">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {UOM_GROUPS.map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel className="text-[10.5px] uppercase tracking-wider">
                        {group.label}
                      </SelectLabel>
                      {group.options.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label="HSN code"
              htmlFor="hsn"
              hint="GST classification. Auto-suggests the tax rate when recognised."
            >
              <Input
                id="hsn"
                value={form.hsnCode ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, hsnCode: e.target.value }))}
                onBlur={(e) => applyHsnRate(e.target.value)}
                placeholder="48191020"
              />
              {hsnRateHint && (
                <p className="mt-1 text-[12px] text-success">{hsnRateHint}</p>
              )}
            </FormField>
            <FormField label="Status" htmlFor="status">
              <Select value={form.status ?? 'ACTIVE'} onValueChange={(v) => setForm((f) => ({ ...f, status: v as ItemCatalogStatus }))}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          {!isService && (
            <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2.5">
              <label className="flex cursor-pointer items-start gap-2 text-[12.5px]">
                <input
                  type="checkbox"
                  checked={form.trackInventory ?? true}
                  onChange={(e) => setForm((f) => ({ ...f, trackInventory: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                />
                <span>
                  <span className="font-medium text-foreground">Track inventory for this item</span>
                  <span className="block text-[11.5px] text-muted-foreground">
                    {isEdit
                      ? 'Disable for items you don\'t want stock counts on (consumables, internal-use). Existing stock movements remain.'
                      : 'Disable for items you don\'t want stock counts on (consumables, internal-use).'}
                  </span>
                </span>
              </label>
            </div>
          )}

          {form.trackInventory && !isService && (
            <FormField label="Reorder point" htmlFor="reorder" hint="Triggers low-stock alert on dashboard.">
              <Input
                id="reorder"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.001"
                value={form.reorderPoint ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, reorderPoint: e.target.value ? Number(e.target.value) : undefined }))
                }
                placeholder="50"
              />
            </FormField>
          )}
        </TabsContent>

        {/* ── Costs (purchase side) ─────────────────────────────── */}
        <TabsContent value="pricing" className="m-0 space-y-5">
          <div className="rounded-md border border-info/20 bg-info/5 px-3 py-2.5 text-[12.5px] text-foreground">
            <span className="font-medium">Cost price is optional but drives margin reports + inventory valuation.</span>{' '}
            <span className="text-muted-foreground">
              For selling prices, see the Sale Prices tab.
            </span>
          </div>

          {!isService && (
            <section className="space-y-3 rounded-md border border-border/70 bg-card px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Purchase information
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label={`Cost price (${tenantCurrency})`}
                  htmlFor="costPrice"
                  hint="Per unit. In the tenant's base currency."
                >
                  <CurrencyInput
                    id="costPrice"
                    value={form.costPrice}
                    onChange={(v) => setForm((f) => ({ ...f, costPrice: v }))}
                  />
                </FormField>
              </div>
              <FormField label="Purchase description" htmlFor="purchaseDescription" hint="Shown on purchase orders to vendors.">
                <Textarea
                  id="purchaseDescription"
                  value={form.purchaseDescription ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, purchaseDescription: e.target.value }))}
                  rows={2}
                  placeholder="Order in cartons of 50. Confirm dimensions before dispatch."
                />
              </FormField>
            </section>
          )}
        </TabsContent>

        {/* ── Tax ────────────────────────────────────────────────── */}
        <TabsContent value="tax" className="m-0 space-y-4">
          <FormField label="Tax preference" htmlFor="taxPref" required>
            <div className="grid grid-cols-2 gap-2" id="taxPref">
              {TAX_PREF_OPTIONS.map((o) => (
                <TypeButton
                  key={o.value}
                  active={form.taxPreference === o.value}
                  label={o.label}
                  hint={o.hint}
                  onClick={() => setForm((f) => ({ ...f, taxPreference: o.value }))}
                />
              ))}
            </div>
          </FormField>

          {form.taxPreference === 'TAXABLE' && isIndiaGst && (
            <>
              <FormField
                label="Intra-state GST rate (%)"
                htmlFor="intraTax"
                hint="Total CGST+SGST when buyer is in the same state as the seller."
              >
                <RateInput
                  id="intraTax"
                  value={form.intraStateTaxRate}
                  onChange={(v) => setForm((f) => ({ ...f, intraStateTaxRate: v }))}
                />
              </FormField>
              <FormField
                label="Inter-state IGST rate (%)"
                htmlFor="interTax"
                hint="When buyer is in a different state. Usually equal to the intra-state total."
              >
                <RateInput
                  id="interTax"
                  value={form.interStateTaxRate}
                  onChange={(v) => setForm((f) => ({ ...f, interStateTaxRate: v }))}
                />
              </FormField>
            </>
          )}
          {form.taxPreference === 'TAXABLE' && !isIndiaGst && (
            <FormField
              label="Tax rate (%)"
              htmlFor="taxRate"
              hint={`Single rate applied per ${tenantTaxRegime === 'NONE' ? 'manual configuration' : tenantTaxRegime.replace('_', ' ')}. Stored as the item's primary rate; the BE applies it on every order regardless of buyer location.`}
            >
              <RateInput
                id="taxRate"
                value={form.intraStateTaxRate}
                onChange={(v) =>
                  // Store the single rate in intraStateTaxRate (the BE
                  // NONE/non-India tax engines read this as the canonical
                  // rate). interStateTaxRate is mirrored so downstream
                  // queries that look at either field stay correct.
                  setForm((f) => ({
                    ...f,
                    intraStateTaxRate: v,
                    interStateTaxRate: v,
                  }))
                }
              />
            </FormField>
          )}
          {form.taxPreference !== 'TAXABLE' && (
            <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2.5 text-[12.5px] text-foreground">
              <span className="font-medium">No tax will be applied to this item.</span>{' '}
              <span className="text-muted-foreground">
                Make sure your customer is correctly classified for{' '}
                {form.taxPreference?.toLowerCase().replace('_', '-').replace('_', ' ')} treatment.
              </span>
            </div>
          )}
        </TabsContent>

        {/* ── Fulfilment ────────────────────────────────────────── */}
        <TabsContent value="fulfilment" className="m-0 space-y-4">
          {isService ? (
            <div className="rounded-md border border-border/60 bg-muted/30 px-4 py-6 text-center">
              <Ruler className="mx-auto h-5 w-5 text-muted-foreground" />
              <div className="mt-2 text-[12.5px] text-muted-foreground">
                Services don't have physical dimensions or weight.
              </div>
            </div>
          ) : (
            <>
              <section className="space-y-3 rounded-md border border-border/70 bg-card px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Dimensions
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <Input
                    type="number" min={0} step="0.001"
                    value={form.dimensionLength ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, dimensionLength: e.target.value ? Number(e.target.value) : undefined }))}
                    placeholder="Length"
                  />
                  <Input
                    type="number" min={0} step="0.001"
                    value={form.dimensionWidth ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, dimensionWidth: e.target.value ? Number(e.target.value) : undefined }))}
                    placeholder="Width"
                  />
                  <Input
                    type="number" min={0} step="0.001"
                    value={form.dimensionHeight ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, dimensionHeight: e.target.value ? Number(e.target.value) : undefined }))}
                    placeholder="Height"
                  />
                  <Select
                    value={form.dimensionUnit ?? 'CM'}
                    onValueChange={(v) => setForm((f) => ({ ...f, dimensionUnit: v as DimensionUnit }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CM">cm</SelectItem>
                      <SelectItem value="MM">mm</SelectItem>
                      <SelectItem value="M">m</SelectItem>
                      <SelectItem value="IN">in</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-[11px] text-muted-foreground">L × W × H — used for shipping cost and dimensional weight calc.</div>
              </section>

              <section className="space-y-3 rounded-md border border-border/70 bg-card px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Weight
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Input
                      type="number" min={0} step="0.001"
                      value={form.weight ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value ? Number(e.target.value) : undefined }))}
                      placeholder="Weight"
                    />
                  </div>
                  <Select
                    value={form.weightUnit ?? 'KG'}
                    onValueChange={(v) => setForm((f) => ({ ...f, weightUnit: v as WeightUnit }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KG">kg</SelectItem>
                      <SelectItem value="G">g</SelectItem>
                      <SelectItem value="LB">lb</SelectItem>
                      <SelectItem value="OZ">oz</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </section>

              <section className="space-y-3 rounded-md border border-border/70 bg-card px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Packing
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="text-[11px] text-muted-foreground">Packaged weight (optional)</div>
                    <Input
                      type="number" min={0} step="0.001"
                      value={form.packagedWeight ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, packagedWeight: e.target.value ? Number(e.target.value) : undefined }))}
                      placeholder={`Gross incl. packaging (${form.weightUnit ?? 'KG'})`}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[11px] text-muted-foreground">Units per carton</div>
                    <Input
                      type="number" min={1} step="1"
                      value={form.unitsPerCarton ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, unitsPerCarton: e.target.value ? Number(e.target.value) : undefined }))}
                      placeholder="e.g. 12"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-[12px] text-foreground">
                    <input
                      type="checkbox"
                      checked={!!form.fragile}
                      onChange={(e) => setForm((f) => ({ ...f, fragile: e.target.checked }))}
                      className="h-4 w-4 rounded border-input"
                    />
                    Fragile
                  </label>
                  <label className="flex items-center gap-2 text-[12px] text-foreground">
                    <input
                      type="checkbox"
                      checked={!!form.hazmat}
                      onChange={(e) => setForm((f) => ({ ...f, hazmat: e.target.checked }))}
                      className="h-4 w-4 rounded border-input"
                    />
                    Hazmat
                  </label>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Packaged weight (if set) is used for shipping; otherwise the item weight.
                  Dimensions above drive the volumetric weight sent to carriers.
                </div>
              </section>
            </>
          )}
        </TabsContent>

        {/* ── Images (gallery + hero) ────────────────────────────── */}
        <TabsContent value="images" className="m-0 space-y-4">
          {isEdit && item?.id ? (
            <ItemImagesTab itemId={item.id} />
          ) : (
            <div className="rounded-md border border-dashed border-border/60 bg-muted/20 p-6 text-center">
              <ImageIcon className="mx-auto h-7 w-7 text-muted-foreground/60" />
              <div className="mt-2 text-[13px] font-semibold">
                Save the item first, then add images
              </div>
              <p className="mt-1 text-[12px] text-muted-foreground">
                Images are attached to an item id — once you fill in the
                General tab and save, this tab will let you upload up to 15
                images per item (JPEG / PNG / WebP / GIF, max 10 MiB each).
                Stored on S3 with Intelligent-Tiering so storage costs scale
                with what actually gets served.
              </p>
            </div>
          )}

          {/* Product video — its own highlighted slot, deliberately apart
              from the image grid above: one clip per item, and the portal
              surfaces it as a distinct media type. */}
          <div className="rounded-lg border border-primary/25 bg-primary/[0.03] p-4">
            <div className="mb-1 flex items-center gap-2">
              <Clapperboard className="h-4 w-4 text-primary" />
              <span className="text-[13px] font-semibold text-foreground">Product video</span>
            </div>
            <p className="mb-3 text-[11.5px] text-muted-foreground">
              One short clip, highlighted on the storefront product page.
            </p>
            <ItemVideoTab itemId={isEdit && item?.id ? item.id : null} />
          </div>
        </TabsContent>

        {/* ── Hero banner (single wide image) ─────────────────────── */}
        <TabsContent value="hero" className="m-0 space-y-6">
          {/* Wide storefront hero (single 3:1 image). */}
          <ItemHeroTab itemId={isEdit && item?.id ? item.id : null} />

          {/* Storefront image placements (tenant-defined aspect ratios). */}
          <div className="border-t border-border/60 pt-5">
            <div className="mb-3 text-[13px] font-semibold text-foreground">
              Storefront images (placements)
            </div>
            <ItemBannersTab itemId={isEdit && item?.id ? item.id : null} />
          </div>
        </TabsContent>

        {/* ── Variants (size / colour / fit / …) ──────────────────── */}
        <TabsContent value="variants" className="m-0 space-y-4">
          {!form.hasVariants ? (
            <div className="rounded-md border border-dashed border-border/60 bg-muted/20 p-6 text-center">
              <Layers className="mx-auto h-7 w-7 text-muted-foreground/60" />
              <div className="mt-2 text-[13px] font-semibold">
                Variants are off for this item
              </div>
              <p className="mt-1 text-[12px] text-muted-foreground">
                Turn on{' '}
                <strong>"This item has variants"</strong> in the{' '}
                <strong>General</strong> tab to manage size / colour combos
                here. Each combo becomes its own SKU with its own stock,
                price overrides and images.
              </p>
            </div>
          ) : isEdit && item?.id ? (
            <ItemVariantsSection
              item={item}
              categoryHint={
                // Prefer the form's CURRENT category selection (operator
                // might have just changed it) over the saved row's. Falls
                // back to the item name so a "MAVK Shirt" item still
                // matches apparel presets even without a category.
                (() => {
                  const selectedId = form.categoryId ?? item.categoryId;
                  const cat = (categoriesQuery.data ?? []).find(
                    (c) => c.id === selectedId,
                  );
                  return [cat?.name, cat?.code, form.name ?? item.name]
                    .filter(Boolean)
                    .join(' ');
                })()
              }
            />
          ) : (
            <div className="rounded-md border border-dashed border-border/60 bg-muted/20 p-6 text-center">
              <Layers className="mx-auto h-7 w-7 text-muted-foreground/60" />
              <div className="mt-2 text-[13px] font-semibold">
                Save the parent item first
              </div>
              <p className="mt-1 text-[12px] text-muted-foreground">
                After save, this tab will let you define attributes
                (Color: Yellow / White / Black, Size: S / M / L / XL / XXL)
                and auto-generate the variant grid.
              </p>
            </div>
          )}
        </TabsContent>

        {/* ── Sale Prices (base + additional currencies) ──────────── */}
        <TabsContent value="prices" className="m-0 space-y-5">
          {form.hasVariants ? (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.05] px-3 py-2.5 text-[12.5px]">
              <span className="font-medium">This item has variants.</span>{' '}
              <span className="text-muted-foreground">
                The price below is the <strong>default</strong> for variants
                that don't set their own. Override per child SKU from the{' '}
                <strong>Variants</strong> tab.
              </span>
            </div>
          ) : (
            <div className="rounded-md border border-info/20 bg-info/5 px-3 py-2.5 text-[12.5px] text-foreground">
              <span className="font-medium">Selling prices drive sales orders.</span>{' '}
              <span className="text-muted-foreground">
                Set the base-currency price below. Add USD/EUR/etc. rows for
                customers transacting in other currencies.
              </span>
            </div>
          )}

          <section className="space-y-3 rounded-md border border-border/70 bg-card px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Base currency · {tenantCurrency}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label={`Selling price (${tenantCurrency})`}
                htmlFor="sellingPrice"
                hint="Per unit, in your tenant's base currency."
              >
                <CurrencyInput
                  id="sellingPrice"
                  value={form.sellingPrice}
                  onChange={(v) => setForm((f) => ({ ...f, sellingPrice: v }))}
                />
              </FormField>
              <FormField
                label={`MRP (${tenantCurrency})`}
                htmlFor="mrp"
                hint="Maximum Retail Price — incl. all taxes. Printed on the dispatch label."
              >
                <CurrencyInput
                  id="mrp"
                  value={form.mrp}
                  onChange={(v) => setForm((f) => ({ ...f, mrp: v }))}
                />
              </FormField>
            </div>
            <FormField label="Sales description" htmlFor="salesDescription" hint="Shown on customer-facing invoices and quotes.">
              <Textarea
                id="salesDescription"
                value={form.salesDescription ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, salesDescription: e.target.value }))}
                rows={2}
                placeholder="High-quality corrugated box, 30×30×30 cm, suitable for packaging."
              />
            </FormField>
          </section>

          <section className="space-y-3 rounded-md border border-border/70 bg-card px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              Additional currencies
            </div>
            {item ? (
              <ItemPricesSection item={item} hideBaseRow />
            ) : (
              <div className="text-[12.5px] text-muted-foreground">
                Save the item first. USD/EUR/etc. price rows are tied to
                a saved item — this section will activate after the draft
                is created.
              </div>
            )}
          </section>
        </TabsContent>

        {/* ── Fabric & Care (apparel composition + wash care) ─────── */}
        <TabsContent value="fabric" className="m-0 space-y-4">
          {isService ? (
            <div className="rounded-md border border-border/60 bg-muted/30 px-4 py-6 text-center">
              <Shirt className="mx-auto h-5 w-5 text-muted-foreground" />
              <div className="mt-2 text-[12.5px] text-muted-foreground">
                Services don't carry fabric composition or wash-care.
              </div>
            </div>
          ) : (
            <FabricCareFields
              value={{
                fabric: form.fabricComposition ?? [],
                careInstructions: form.washCareInstructions ?? [],
                careNotes: form.careNotes ?? '',
              }}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  fabricComposition: v.fabric,
                  washCareInstructions: v.careInstructions,
                  careNotes: v.careNotes,
                }))
              }
            />
          )}
        </TabsContent>

        {/* ── Units of measure (base + alternate UoMs) ───────────── */}
        <TabsContent value="units" className="m-0 space-y-4">
          {isService ? (
            <div className="rounded-md border border-border/60 bg-muted/30 px-4 py-6 text-center">
              <Scale className="mx-auto h-5 w-5 text-muted-foreground" />
              <div className="mt-2 text-[12.5px] text-muted-foreground">
                Services are sold by the engagement, not by unit.
              </div>
            </div>
          ) : isEdit && item ? (
            // Editing a saved item — manage alternate UoMs live against the
            // item's units sub-resource.
            <ItemUnitsSection item={item} />
          ) : (
            // Creating — buffer alternate UoMs; they're POSTed after the
            // item is created (see the submit mutation).
            <DraftUnitsEditor
              baseUnit={(form.unitOfMeasure || 'EA').trim().toUpperCase()}
              units={draftUnits}
              onChange={setDraftUnits}
            />
          )}
        </TabsContent>
    </FormSheet>
    <ConfirmDialog
      open={!!renameConfirm}
      onOpenChange={(o) => !o && setRenameConfirm(null)}
      title={
        renameConfirm
          ? `Rename SKU "${renameConfirm.oldSku}" to "${renameConfirm.newSku}"?`
          : ''
      }
      description={
        renameConfirm ? (
          <>
            {renameConfirm.childCount > 0 ? (
              <>
                This will rename <strong>{renameConfirm.childCount}</strong>{' '}
                child variant
                {renameConfirm.childCount === 1 ? '' : 's'} too.
                {' '}
              </>
            ) : null}
            Existing stock, orders, and barcodes that reference the old SKU
            will need to be updated externally.
          </>
        ) : (
          ''
        )
      }
      confirmLabel="Rename SKU"
      onConfirm={async () => {
        await mutation.mutateAsync();
        setRenameConfirm(null);
      }}
    />
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────────────

function TypeButton({
  active,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-2.5 text-left transition-colors',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border bg-card hover:bg-muted/50',
      )}
    >
      <div className="text-[13px] font-semibold">{label}</div>
      <div className="mt-0.5 text-[11.5px] text-muted-foreground">{hint}</div>
    </button>
  );
}

function CurrencyInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: number | undefined | null;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <div className="flex">
      <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted/40 px-3 text-[12.5px] font-medium text-muted-foreground">
        ₹
      </span>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min={0}
        step="0.01"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        placeholder="0.00"
        className="rounded-l-none font-mono tabular-nums"
      />
    </div>
  );
}

function RateInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: number | undefined | null;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {GST_SLABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            className={
              value === s
                ? 'rounded-md border border-primary bg-primary/10 px-2.5 py-1 text-[12px] font-medium text-primary'
                : 'rounded-md border border-border bg-background px-2.5 py-1 text-[12px] text-muted-foreground transition-colors hover:bg-muted/50'
            }
          >
            {s}%
          </button>
        ))}
      </div>
      <div className="flex">
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step="0.01"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
          placeholder="0"
          className="rounded-r-none font-mono tabular-nums"
        />
        <span className="inline-flex items-center rounded-r-md border border-l-0 border-input bg-muted/40 px-3 text-[12.5px] font-medium text-muted-foreground">
          %
        </span>
      </div>
    </div>
  );
}

function emptyToUndef(s: string | undefined | null): string | undefined {
  return s && s.trim() ? s.trim() : undefined;
}

// ── Alternate UoMs at create time ─────────────────────────────────────
// `ItemUnit`s are a post-create sub-resource (POST /items/:id/units), so at
// create we buffer draft rows and persist them after the item exists. In
// edit mode the live ItemUnitsSection is used instead. `conversionFactor`
// stays a raw string while typing; it's coerced on submit.

interface DraftUnit {
  code: string;
  name: string;
  conversionFactor: string;
  isPurchaseDefault: boolean;
  isSalesDefault: boolean;
}

/** A buffered unit is persisted only when complete + sane. Mirrors the
 *  detail-sheet CreateUnitForm's validation so buffered rows behave the
 *  same as live-added ones. */
function draftUnitValid(u: DraftUnit, baseUnit: string): boolean {
  const factor = Number(u.conversionFactor);
  return (
    u.code.trim().length > 0 &&
    u.name.trim().length > 0 &&
    u.conversionFactor.trim().length > 0 &&
    Number.isFinite(factor) &&
    factor > 0 &&
    u.code.trim().toUpperCase() !== baseUnit.toUpperCase()
  );
}

function DraftUnitsEditor({
  baseUnit,
  units,
  onChange,
}: {
  baseUnit: string;
  units: DraftUnit[];
  onChange: (units: DraftUnit[]) => void;
}) {
  const add = () =>
    onChange([
      ...units,
      { code: '', name: '', conversionFactor: '', isPurchaseDefault: false, isSalesDefault: false },
    ]);
  const setRow = (i: number, patch: Partial<DraftUnit>) =>
    onChange(units.map((u, idx) => (idx === i ? { ...u, ...patch } : u)));
  const remove = (i: number) => onChange(units.filter((_, idx) => idx !== i));
  // The backend keeps a single purchase/sales default per item; enforce
  // that in the buffer so we never POST two competing defaults.
  const setPurchaseDefault = (i: number, checked: boolean) =>
    onChange(
      units.map((u, idx) => ({
        ...u,
        isPurchaseDefault: idx === i ? checked : checked ? false : u.isPurchaseDefault,
      })),
    );
  const setSalesDefault = (i: number, checked: boolean) =>
    onChange(
      units.map((u, idx) => ({
        ...u,
        isSalesDefault: idx === i ? checked : checked ? false : u.isSalesDefault,
      })),
    );

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-md border border-info/25 bg-info/[0.05] px-3 py-2.5 text-[12px]">
        <Scale className="mt-0.5 h-4 w-4 shrink-0 text-info" />
        <div className="space-y-1 text-muted-foreground">
          <p className="font-medium text-foreground">
            Base unit: <span className="font-mono">{baseUnit}</span> — stock is always stored in base
          </p>
          <p>
            Add alternate units you buy or sell in (e.g. 1 BOX = 12 {baseUnit}) and tag one as the
            default for purchases / sales. These save with the item.
          </p>
        </div>
      </div>

      {units.length === 0 ? (
        <p className="rounded-md border border-dashed border-border/70 px-3 py-4 text-center text-[12px] text-muted-foreground">
          No alternate units yet. Add one to receive in cases / boxes / rolls while stock stays in{' '}
          <span className="font-mono">{baseUnit}</span>.
        </p>
      ) : (
        <div className="space-y-2">
          {units.map((u, i) => {
            const codeClash =
              u.code.trim().length > 0 &&
              u.code.trim().toUpperCase() === baseUnit.toUpperCase();
            const factorNum = Number(u.conversionFactor);
            const badFactor =
              u.conversionFactor.trim().length > 0 &&
              (!Number.isFinite(factorNum) || factorNum <= 0);
            return (
              <div key={i} className="rounded-md border border-border/70 bg-card p-3">
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-3">
                    <label className="text-[11px] text-muted-foreground">Code</label>
                    <Input
                      value={u.code}
                      onChange={(e) => setRow(i, { code: e.target.value.toUpperCase() })}
                      placeholder="BOX"
                      className="mt-1 h-8 font-mono text-[12.5px]"
                      maxLength={16}
                    />
                  </div>
                  <div className="col-span-5">
                    <label className="text-[11px] text-muted-foreground">Name</label>
                    <Input
                      value={u.name}
                      onChange={(e) => setRow(i, { name: e.target.value })}
                      placeholder="Box of 12"
                      className="mt-1 h-8 text-[12.5px]"
                    />
                  </div>
                  <div className="col-span-4">
                    <label className="text-[11px] text-muted-foreground">
                      1 {u.code.trim() || 'unit'} ={' '}
                      <span className="text-foreground">
                        ? <span className="font-mono">{baseUnit}</span>
                      </span>
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.000001"
                      value={u.conversionFactor}
                      onChange={(e) => setRow(i, { conversionFactor: e.target.value })}
                      placeholder="12"
                      className="mt-1 h-8 text-right font-mono text-[12.5px] tabular-nums"
                    />
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border/60 pt-3 text-[12px]">
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={u.isPurchaseDefault}
                      onChange={(e) => setPurchaseDefault(i, e.target.checked)}
                    />
                    <ArrowDownToLine className="h-3 w-3 text-info" />
                    <span>Default for purchases</span>
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={u.isSalesDefault}
                      onChange={(e) => setSalesDefault(i, e.target.checked)}
                    />
                    <ShoppingCart className="h-3 w-3 text-success" />
                    <span>Default for sales</span>
                  </label>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="ml-auto h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(i)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {(codeClash || badFactor) && (
                  <p className="mt-2 text-[11px] text-warning">
                    {codeClash
                      ? `Code can't match the base unit (${baseUnit}).`
                      : 'Conversion factor must be greater than 0.'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Button type="button" size="sm" variant="outline" onClick={add}>
        <Plus className="h-3.5 w-3.5" />
        Add unit
      </Button>
    </div>
  );
}
